import { request } from "components/hooks/useQuery";
import { PageSlug } from "types/base";
import { createSubscription } from "components/hooks/usePreview";
import { GetStaticPropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import React from "react";
import Container from "components/core/Container";
import { withAppLayout } from "components/core/withAppLayout";
import nextI18NextConfig from "./../../../../next-i18next.config";
import { ITextBlock } from "blocks/text/TextBlock";
import useTrans from "components/hooks/useTrans";
import { getAllFormsPages, getFormsPage } from "queries/Forms";
import Logo from "components/base/Logo";
import Link from "components/base/Link";
import Paragraph from "components/elements/Paragraph";
import Close from "assets/svg/icons/close.svg";
import TextBlockContent from "blocks/text/TextBlockContent";
import { Image } from "react-datocms";
import { ICompaniesList } from "blocks/companies/CompaniesList";
import Form, { IFormBlock } from "blocks/form/Form";

export async function getStaticPaths({ locales }: any) {
    const { allFormPages } = await request({
        query: getAllFormsPages
    });

    const paths = allFormPages.map(({ slug }: any) => {
        return locales.map((locale: any) => ({
            params: { slug: slug }, locale: locale
        })).flat();
    }).flat();

    return {
        paths: paths,
        fallback: false
    };
}

export async function getStaticProps(context: GetStaticPropsContext) {
    const params = context.params as PageSlug;

    const query = {
        query: getFormsPage(params.slug, context.locale, context.locales),
        preview: context.preview,
        withAppData: true,
        locale: context.locale
    };

    let data = await createSubscription(context, query);

    if (!data?.initialData?.formPage) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            data: data,
            hideHeader: true,
            hideFooter: true,
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        },
    };
}

export interface IFormPage {
    id: string,
    title: string,
    slug: string,
    form: IFormBlock[],
    content: ITextBlock[],
    companies: ICompaniesList[],
    thankYou: ITextBlock[],
}

type Props = {
    formPage: IFormPage
};

function FormSection(props: Props) {
    const { form, content, companies, slug } = props.formPage;
    const { t } = useTrans();

    return (
        <div className={'top-0 w-full z-50 bg-purple-tint-lighter overflow-scroll lg:overflow-auto lg:bg-white mb-0 h-screen lg:h-auto lg:mb-32 -mt-8 lg:-mt-24 '}>
            <div className={"bg-white mb-10 lg:mb-0 lg:mt-11"}>
                <Container>
                    <nav className={'flex justify-between py-5'}>
                        <Logo/>
                        <Link to={''} className={'my-auto cursor-pointer'}>
                            <div className={'flex flex-row gap-x-5'}>
                                <Paragraph size={'medium'} className={'my-auto'}>
                                    {t('returnHome')}
                                </Paragraph>
                                <div className={"flex self-center"}>
                                    <Close/>
                                </div>
                            </div>
                        </Link>
                    </nav>
                </Container>
            </div>

            <Container>
                <div className={"lg:flex lg:mt-20"}>
                    <section className={"lg:flex-1"}>
                        {content.map((block: ITextBlock) => (
                            <TextBlockContent
                                key={block.id}
                                {...block}
                                className={"mb-2 mt-8 lg:mb-0 lg:mt-0 pr-0 lg:pr-20 xl:pr-52 "}
                            />
                        ))}
                        <div className={"mt-8 lg:inline-block w-full hidden"}>
                            {companies.map(list => (
                                <div key={list?.title}>
                                    <Paragraph
                                        color={'purple-dark'}
                                        className={'opacity-40 uppercase tracking-widest'}
                                        fontWeight={"medium"}
                                        size={"extra-small"}
                                    >
                                        {list?.title}
                                    </Paragraph>
                                    <div className={'grid grid-row-3 grid-cols-4 lg:mt-5 w-full'}>
                                        {list?.logos?.map((logo, index) => {
                                            return (
                                                <Image
                                                    key={logo.id + index}
                                                    data={logo.responsiveImage}
                                                    className={'place-self-start h-12'}
                                                    pictureClassName={'-ml-4 place-self-start object-contain'}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    {form.map(f => <Form key={f.id} slug={slug} {...f} />)}
                </div>
            </Container>
        </div>
    );
}

export default withAppLayout(FormSection);